/**
 * @file MenuEditor.tsx
 * @description Содержимое экрана «Сайт → Меню» (phase19.16 Р1; SB15–SB17,
 * SB76, SB81; uiux-design-r1 §3): вкладки «Шапка» и «Подвал» (до 1024 —
 * ещё «Меню телефона»), дерево пунктов, окно пункта, предпросмотр меню
 * телефона справа, подсказки и пустые состояния.
 *
 * Приёмка Р2а (SB113, П3; uiux-footer-columns §4.6, §4.7): «Подвал» —
 * колонками (`FooterColumnsEditor`), вкладка «Подвал · N из 3». В подвале
 * пункт всегда в колонке: «Добавить пункт» без колонки нет (сервер такой
 * пункт отклоняет — `site_menu_footer_loose_item`), окно пункта не
 * спрашивает «Где».
 *
 * «SEO-техника» v0.19.65 (SB146; uiux-seo-tech §3.3): вкладка «Крошки»
 * (`BreadcrumbsTab`, `sub=crumbs`) — «Показывать хлебные крошки» сайта.
 */

import { footerLayout, SITE_LIMITS, type SiteMenuLocation } from "@chaiberi/shared";
import { Plus } from "lucide-react";
import { useContext, useState } from "react";

import { Button } from "../../ui/button";
import { ConfirmDialog } from "../../ui/ConfirmDialog";
import { ListError } from "../../ui/ListStates";
import { NoticeStrip } from "../../ui/NoticeStrip";
import { Skeleton } from "../../ui/skeleton";
import { Tabs } from "../../ui/Tabs";
import { useConfirm } from "../../ui/use-confirm";
import { SiteViewContext } from "../site-view-context";
import { BreadcrumbsTab } from "./BreadcrumbsTab";
import { FooterColumnsEditor } from "./FooterColumnsEditor";
import { menuRowActions } from "./menu-actions";
import { MenuItemDialog, type MenuDialogMode } from "./MenuItemDialog";
import { MenuLocationPanel } from "./MenuLocationPanel";
import { MobileMenuPreview } from "./MobileMenuPreview";
import { useSiteMenus } from "../../../hooks/site-view/useSiteMenus";
import { BELOW_LG_QUERY, useMediaQuery } from "../../../hooks/useMediaQuery";
import { emptyMenuForm, menuFieldsFromForm } from "../../../lib/site-view/menu-form";
import { countItems } from "../../../lib/site-view/menu-tree";
import type { SiteMenuItem, SiteViewDraft } from "../../../lib/site-view/types";

export type MenuEditorTab = SiteMenuLocation | "PHONE" | "CRUMBS";
type Tab = MenuEditorTab;

interface Props {
  draft: SiteViewDraft;
  /** Узкая колонка конструктора: меню телефона — вкладкой, а не сбоку (признак от места, не от окна). */
  narrow?: boolean;
  /** Вкладку задаёт конструктор (выбор в адресе). */
  tab?: Tab;
  onTab?: (tab: Tab) => void;
}

export function MenuEditor({ draft, narrow: forceNarrow = false, tab: outerTab, onTab }: Props) {
  const api = useSiteMenus();
  const narrowWindow = useMediaQuery(BELOW_LG_QUERY);
  const narrow = forceNarrow || narrowWindow;
  const confirm = useConfirm();
  // Черновик оформления (если панель внутри каркаса вида сайта): свежий шаблон подвала и «Переключить».
  const themeDraft = useContext(SiteViewContext)?.parts?.theme ?? null;
  const [ownTab, setOwnTab] = useState<Tab>("HEADER");
  const tab = outerTab ?? ownTab;
  const setTab = onTab ?? setOwnTab;
  const [dialog, setDialog] = useState<MenuDialogMode | null>(null);

  if (api.error && !api.menus) return <ListError message={api.error} onRetry={() => void api.reload()} />;
  if (!api.menus) return <Skeleton className="h-64 rounded-xl" aria-label="Загрузка меню" />;
  const menus = api.menus;
  const location: SiteMenuLocation = tab === "FOOTER" ? "FOOTER" : "HEADER";
  const tree = menus[location];
  // Колонки подвала — тем же счётом, что сервер и витрина (SB113).
  const columns = footerLayout(menus.FOOTER).columns.length;
  const header = draft.theme.header;
  const footer = themeDraft?.theme.footer ?? draft.theme.footer;
  const logoAfter = location === "HEADER" && header.logoPosition === "center" ? (header.splitAt ?? Math.ceil(tree.length / 2)) : null;

  async function onDelete(item: SiteMenuItem) {
    const kids = item.children.length;
    const ok = await confirm.confirm({
      title: `Удалить пункт «${item.label ?? item.target.title}»?`,
      description: kids > 0 ? `Вместе с ним удалятся вложенные пункты: ${kids}. Сайт не изменится до публикации.` : "Сайт не изменится до публикации.",
      action: "Удалить",
      danger: true,
    });
    if (ok) await api.remove(item.id);
  }

  // «Каталог» и «Лента блога» — по очереди: у каждой правки своя версия меню.
  async function addDefaults() {
    const catalog = { ...emptyMenuForm("SYSTEM"), label: "Каталог", systemRoute: "CATALOG" as const };
    if (!(await api.create("HEADER", null, menuFieldsFromForm(catalog)))) return;
    await api.create("HEADER", null, menuFieldsFromForm({ ...emptyMenuForm("SYSTEM"), label: "Лента блога", systemRoute: "BLOG" }));
  }

  const handlers = {
    edit: (item: SiteMenuItem, nested: boolean) => setDialog({ kind: "edit", item, nested }),
    reorder: (next: SiteMenuItem[]) => void api.reorder(location, next),
    toggleHidden: (item: SiteMenuItem) => void api.update(item.id, { isHidden: !item.isHidden }),
    remove: (item: SiteMenuItem) => void onDelete(item),
  };
  const tabs = [
    { value: "HEADER" as Tab, label: "Шапка", count: countItems(menus.HEADER) },
    { value: "FOOTER" as Tab, label: `Подвал · ${columns} из ${SITE_LIMITS.footerColumns}` },
    { value: "CRUMBS" as Tab, label: "Крошки" },
    ...(narrow ? [{ value: "PHONE" as Tab, label: "Меню телефона" }] : []),
  ];
  const phone = <MobileMenuPreview siteName={draft.brand.siteName} header={menus.HEADER} footer={menus.FOOTER} contacts={draft.brand.contacts} />;
  const headerPanel = (
    <>
      <Button variant="outline" className="self-start" onClick={() => setDialog({ kind: "create", location, parentId: null })}>
        <Plus aria-hidden />
        Добавить пункт
      </Button>
      <MenuLocationPanel
        location={location}
        tree={tree}
        columns={columns}
        logoAfter={logoAfter}
        actionsFor={(item, nested) => menuRowActions(tree, item, nested, handlers)}
        onReorder={handlers.reorder}
        onAddDefaults={() => void addDefaults()}
      />
    </>
  );

  return (
    <div className="flex flex-col gap-4">
      <Tabs aria-label="Меню" items={tabs} value={tab} onChange={setTab} />
      {tab === "CRUMBS" ? (
        <BreadcrumbsTab />
      ) : tab === "PHONE" ? (
        phone
      ) : (
        <div className={narrow ? "flex flex-col gap-6" : "grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_22.5rem]"}>
          <div className="flex min-w-0 flex-col gap-3">
            {location === "FOOTER" ? (
              <FooterColumnsEditor
                api={api}
                tree={tree}
                footer={footer}
                brand={draft.brand}
                narrow={narrow}
                confirm={confirm.confirm}
                onEdit={(item) => setDialog({ kind: "edit", item, nested: true })}
                onAddItem={(parentId) => setDialog({ kind: "create", location: "FOOTER", parentId, lockParent: true })}
                onColumnsTemplate={themeDraft ? () => themeDraft.update("footer", { template: "columns" }) : undefined}
              />
            ) : (
              headerPanel
            )}
            <NoticeStrip>
              Оферту, политику ПДн, условия возврата, доставки и оплаты и вход в кабинет ставит платформа — добавлять их не нужно.
            </NoticeStrip>
          </div>
          {narrow ? null : (
            <aside aria-label="Меню телефона" className="flex flex-col gap-2">
              <span className="type-caption text-on-surface-variant">Меню телефона (360)</span>
              {phone}
              <p className="type-caption text-on-surface-variant">Скрытые пункты не показываются. Порядок: шапка → подвал → вход → контакты.</p>
            </aside>
          )}
        </div>
      )}
      <MenuItemDialog
        mode={dialog}
        parents={tree.filter((i) => i.linkType !== "CATALOG_CATEGORIES")}
        onClose={() => setDialog(null)}
        onSubmit={(form, parentId) =>
          dialog?.kind === "edit"
            ? api.update(dialog.item.id, menuFieldsFromForm(form))
            : api.create(dialog?.kind === "create" ? dialog.location : location, parentId, menuFieldsFromForm(form))
        }
      />
      <ConfirmDialog request={confirm.request} onSettle={confirm.settle} />
    </div>
  );
}
